import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import confetti from "canvas-confetti";
import { BalloonSvg } from "./SponsoredBalloons";
import { VEHICLE_ART } from "./SkyVehicles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHIPS = [
  { id: "balloon", w: 52, lane: "10%", delay: 0 },
  { id: "hot_air", w: 68, lane: "22%", delay: 2.2 },
  { id: "drone", w: 56, lane: "16%", delay: 4.4 },
  { id: "ufo", w: 64, lane: "28%", delay: 6.6 },
  { id: "rocket", w: 50, lane: "13%", delay: 8.8 },
  { id: "blimp", w: 110, lane: "34%", delay: 11 },
];
const CROSS_SECONDS = 13;
const PARADE_MS = (11 + CROSS_SECONDS + 2) * 1000;
const FIRST_AFTER_MS = 75000;
const EVERY_MS = 12 * 60 * 1000;

// 🎺 SKY PARADE — the whole fleet crosses the map; tap every ship for bonus bucks!
export const SkyParade = () => {
  const [active, setActive] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [tapped, setTapped] = useState({});
  const [toast, setToast] = useState(null);
  const toastT = useRef(null);
  const endT = useRef(null);
  const busyRef = useRef(false);

  useEffect(() => {
    const testMode = window.location.search.includes("parade");
    const announce = () => {
      if (busyRef.current) return; // already counting down / parading — never double-book
      busyRef.current = true;
      setCountdown(10);
    };
    const first = setTimeout(announce, testMode ? 3000 : FIRST_AFTER_MS);
    const loop = setInterval(announce, EVERY_MS);
    return () => { clearTimeout(first); clearInterval(loop); clearTimeout(endT.current); };
  }, []);

  useEffect(() => {
    if (countdown === null) return undefined;
    if (countdown <= 0) {
      setCountdown(null);
      setTapped({});
      setActive(true);
      clearTimeout(endT.current);
      endT.current = setTimeout(() => {
        setActive(false);
        busyRef.current = false;
      }, PARADE_MS);
      return undefined;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const say = (msg) => {
    setToast(msg);
    clearTimeout(toastT.current);
    toastT.current = setTimeout(() => setToast(null), 3200);
  };

  const tap = async (ship, lane) => {
    setTapped((t) => ({ ...t, [ship]: true }));
    confetti({ particleCount: 26, spread: 50, scalar: 0.7, origin: { x: 0.5, y: parseFloat(lane) / 100 + 0.06 } });
    try {
      const r = await axios.post(`${API}/sky-parade/tap`, { ship }, { withCredentials: true });
      say(r.data.message);
      if (r.data.bonus > 0) confetti({ particleCount: 140, spread: 90, origin: { y: 0.5 } });
    } catch { say("🎺 POP!"); }
  };

  return (
    <AnimatePresence>
      {countdown !== null && (
        <motion.div data-testid="parade-countdown"
          initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
          className="absolute top-24 sm:top-28 left-1/2 -translate-x-1/2 z-[1850] glass-strong border border-sky-400/50 rounded-full px-5 py-2 shadow-2xl pointer-events-none">
          <span className="font-heading font-extrabold text-sm sm:text-base text-sky-200">🎺 SKY PARADE incoming in <span data-testid="parade-countdown-secs" className="text-amber-300">{countdown}s</span> — get ready to tap!</span>
        </motion.div>
      )}
      {active && (
        <>
          <motion.div data-testid="sky-parade-banner"
            initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
            className="absolute top-24 sm:top-28 left-1/2 -translate-x-1/2 z-[1850] glass-strong border border-amber-400/50 rounded-full px-5 py-2 shadow-2xl pointer-events-none">
            <span className="font-heading font-extrabold text-sm sm:text-base bg-gradient-to-r from-amber-300 via-rose-400 to-violet-400 bg-clip-text text-transparent">🎺 SKY PARADE! Tap every ship!</span>
          </motion.div>
          {SHIPS.map((s) => {
            if (tapped[s.id]) return null;
            const Art = s.id === "balloon" ? null : VEHICLE_ART[s.id];
            return (
              <motion.button key={s.id} data-testid={`parade-ship-${s.id}`}
                onClick={() => tap(s.id, s.lane)}
                initial={{ x: "-20vw", opacity: 0 }}
                animate={{ x: "115vw", opacity: 1, y: [0, 8, -8, 4, 0] }}
                exit={{ opacity: 0, scale: 1.5 }}
                transition={{ x: { duration: CROSS_SECONDS, ease: "linear", delay: s.delay },
                  opacity: { duration: 0.4, delay: s.delay },
                  y: { duration: CROSS_SECONDS, ease: "easeInOut", delay: s.delay } }}
                className="absolute z-[1836] pointer-events-auto select-none cursor-pointer"
                style={{ top: s.lane, left: 0 }}>
                {Art ? <Art w={s.w} /> : <BalloonSvg color="amber" w={s.w} />}
              </motion.button>
            );
          })}
          {toast && (
            <motion.div data-testid="parade-toast" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="absolute bottom-32 left-1/2 -translate-x-1/2 z-[1860] bg-slate-900/95 border border-amber-400/50 rounded-full px-4 py-2 text-sm font-heading font-bold text-amber-200 shadow-xl pointer-events-none whitespace-nowrap">
              {toast}
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>
  );
};
